import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import '../styles/QuizAdmin.css';

// Icônes SVG inline
const IconHistory = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 3v18h18" />
    <path d="M19 9l-5 5-4-4-3 3" />
  </svg>
);

const IconCardinal = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
    <circle cx="9" cy="7" r="4"></circle>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
  </svg>
);

const IconRitual = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
    <path d="M2 17l10 5 10-5"></path>
    <path d="M2 12l10 5 10-5"></path>
  </svg>
);

const IconGlobe = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="2" y1="12" x2="22" y2="12"></line>
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
  </svg>
);

const IconEdit = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
  </svg>
);

const IconDelete = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
    <line x1="10" y1="11" x2="10" y2="17"></line>
    <line x1="14" y1="11" x2="14" y2="17"></line>
  </svg>
);

const QuizAdmin = () => {
  // États pour la gestion des questions
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // État pour le filtrage et la recherche
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('');
  
  // État pour l'édition
  const [editMode, setEditMode] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  
  // Configuration initiale d'une nouvelle question
  const emptyQuestion = {
    id: '',
    question: '',
    options: ['', '', '', ''],
    correctAnswer: 0,
    category: 'histoire',
    difficulty: 'débutant',
    explanation: ''
  };
  
  // Catégories et difficultés disponibles
  const categories = [
    { id: 'histoire', name: 'Histoire du Conclave', icon: <IconHistory /> },
    { id: 'cardinaux', name: 'Collège des Cardinaux', icon: <IconCardinal /> },
    { id: 'traditions', name: 'Traditions & Procédures', icon: <IconRitual /> },
    { id: 'géographie', name: 'Géographie Vaticane', icon: <IconGlobe /> }
  ];
  
  const difficulties = [
    { id: 'débutant', name: 'Débutant', color: '#4caf50' },
    { id: 'intermédiaire', name: 'Intermédiaire', color: '#ff9800' },
    { id: 'expert', name: 'Expert', color: '#f44336' }
  ];
  
  // Charger les questions au démarrage
  useEffect(() => {
    fetchQuestions();
  }, []);
  
  // Charger toutes les questions
  const fetchQuestions = async () => {
    try {
      setLoading(true);
      // Dans une implémentation réelle, ceci serait un appel API
      // Pour le prototype, nous utilisons des questions statiques
      
      // Simuler un délai de chargement
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Questions par défaut (pour le prototype)
      const sampleQuestions = [
        {
          id: 'h1',
          question: "Quel pape a démissionné en 2013, créant la nécessité d'un conclave extraordinaire ?",
          options: ["Jean-Paul II", "Benoît XVI", "François", "Jean XXIII"],
          correctAnswer: 1,
          category: 'histoire',
          difficulty: 'débutant',
          explanation: "Benoît XVI, né Joseph Ratzinger, a démissionné le 28 février 2013, devenant le premier pape à démissionner depuis Grégoire XII en 1415."
        },
        {
          id: 'h2',
          question: "Combien de tours a nécessité l'élection du Pape François en 2013 ?",
          options: ["3", "5", "9", "12"],
          correctAnswer: 1,
          category: 'histoire',
          difficulty: 'intermédiaire',
          explanation: "Le cardinal Jorge Mario Bergoglio a été élu au 5ème tour de scrutin, le deuxième jour du conclave."
        },
        {
          id: 'c1',
          question: "Quel âge maximum un cardinal doit-il avoir pour pouvoir voter lors d'un conclave ?",
          options: ["70 ans", "75 ans", "80 ans", "85 ans"],
          correctAnswer: 2,
          category: 'cardinaux',
          difficulty: 'débutant',
          explanation: "Les cardinaux de moins de 80 ans à la date du début de la vacance du Saint-Siège peuvent participer à l'élection."
        },
        {
          id: 't1',
          question: "Où se déroule traditionnellement le conclave ?",
          options: ["Basilique Saint-Pierre", "Chapelle Sixtine", "Castel Gandolfo", "Palais apostolique"],
          correctAnswer: 1,
          category: 'traditions',
          difficulty: 'débutant',
          explanation: "Le conclave se déroule dans la Chapelle Sixtine du Vatican."
        },
        {
          id: 'g1',
          question: "Dans quel pays se trouve la Cité du Vatican ?",
          options: ["France", "Espagne", "Italie", "Suisse"],
          correctAnswer: 2,
          category: 'géographie',
          difficulty: 'débutant',
          explanation: "La Cité du Vatican est une cité-État enclavée dans Rome, en Italie."
        }
      ];
      
      setQuestions(sampleQuestions);
      setLoading(false);
    } catch (err) {
      console.error('Erreur lors du chargement des questions:', err);
      setError('Erreur lors du chargement des questions');
      setLoading(false);
    }
  };
  
  // Filtrer les questions en fonction des critères
  const filteredQuestions = questions.filter(question => {
    const matchesSearch = question.question.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter ? question.category === categoryFilter : true;
    const matchesDifficulty = difficultyFilter ? question.difficulty === difficultyFilter : true;
    
    return matchesSearch && matchesCategory && matchesDifficulty;
  });
  
  // Créer une nouvelle question
  const createQuestion = () => {
    setCurrentQuestion({
      ...emptyQuestion,
      id: `q${Date.now()}` // Générer un ID unique basé sur l'horodatage
    });
    setEditMode(true);
  };
  
  // Éditer une question existante
  const editQuestion = (question) => {
    setCurrentQuestion({ ...question });
    setEditMode(true);
  };
  
  // Supprimer une question
  const deleteQuestion = async (questionId) => {
    // Demander confirmation
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette question ?')) {
      return;
    }
    
    try {
      // Dans une implémentation réelle, ceci serait un appel API DELETE
      // Pour le prototype, nous mettons à jour l'état local
      
      // Simuler un délai de suppression
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Mettre à jour l'état local
      setQuestions(questions.filter(q => q.id !== questionId));
      setSuccessMessage('Question supprimée avec succès');
      
      // Masquer le message après 3 secondes
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      console.error('Erreur lors de la suppression de la question:', err);
      setError('Erreur lors de la suppression de la question');
      
      // Masquer le message d'erreur après 3 secondes
      setTimeout(() => {
        setError(null);
      }, 3000);
    }
  };
  
  // Annuler l'édition
  const cancelEdit = () => {
    setCurrentQuestion(null);
    setEditMode(false);
  };
  
  // Sauvegarder une question (nouvelle ou modifiée)
  const saveQuestion = async () => {
    // Validation des données
    if (!currentQuestion.question.trim()) {
      setError('Veuillez entrer une question');
      return;
    }
    
    if (currentQuestion.options.some(option => !option.trim())) {
      setError('Veuillez remplir toutes les options');
      return;
    }
    
    if (!currentQuestion.explanation.trim()) {
      setError('Veuillez entrer une explication');
      return;
    }
    
    try {
      // Dans une implémentation réelle, ceci serait un appel API POST ou PUT
      // Pour le prototype, nous mettons à jour l'état local
      
      // Simuler un délai de sauvegarde
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Vérifier si c'est une nouvelle question ou une mise à jour
      if (questions.some(q => q.id === currentQuestion.id)) {
        // Mise à jour d'une question existante
        setQuestions(questions.map(q => 
          q.id === currentQuestion.id ? currentQuestion : q
        ));
        setSuccessMessage('Question mise à jour avec succès');
      } else {
        // Création d'une nouvelle question
        setQuestions([...questions, currentQuestion]);
        setSuccessMessage('Nouvelle question créée avec succès');
      }
      
      // Réinitialiser l'état d'édition
      setCurrentQuestion(null);
      setEditMode(false);
      
      // Masquer le message après 3 secondes
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      console.error('Erreur lors de la sauvegarde de la question:', err);
      setError('Erreur lors de la sauvegarde de la question');
      
      // Masquer le message d'erreur après 3 secondes
      setTimeout(() => {
        setError(null);
      }, 3000);
    }
  };
  
  // Gérer les changements dans le formulaire d'édition
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentQuestion({
      ...currentQuestion,
      [name]: value
    });
  };
  
  // Gérer les changements dans les options
  const handleOptionChange = (index, value) => {
    const updatedOptions = [...currentQuestion.options];
    updatedOptions[index] = value;
    
    setCurrentQuestion({
      ...currentQuestion,
      options: updatedOptions
    });
  };
  
  // Formater le texte de la catégorie
  const getCategoryText = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : categoryId;
  };
  
  // Formater le texte de la difficulté
  const getDifficultyText = (difficultyId) => {
    const difficulty = difficulties.find(diff => diff.id === difficultyId);
    return difficulty ? difficulty.name : difficultyId;
  };
  
  // Obtenir la couleur de la difficulté
  const getDifficultyColor = (difficultyId) => {
    const difficulty = difficulties.find(diff => diff.id === difficultyId);
    return difficulty ? difficulty.color : '#999';
  };
  
  // Obtenir l'icône de la catégorie
  const getCategoryIcon = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.icon : null;
  };
  
  return (
    <div className="quiz-admin-page">
      <h1>Administration du Quiz</h1>
      
      {/* Affichage des messages */}
      {error && <div className="message error">{error}</div>}
      {successMessage && <div className="message success">{successMessage}</div>}
      
      {/* Mode édition */}
      {editMode ? (
        <div className="edit-question-section">
          <h2>{currentQuestion.id ? 'Modifier la question' : 'Créer une nouvelle question'}</h2>
          
          <div className="question-form">
            <div className="form-group">
              <label>Question</label>
              <input 
                type="text" 
                name="question" 
                value={currentQuestion.question} 
                onChange={handleInputChange} 
                placeholder="Entrez la question" 
                required 
              />
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label>Catégorie</label>
                <select 
                  name="category" 
                  value={currentQuestion.category} 
                  onChange={handleInputChange}
                >
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label>Difficulté</label>
                <select 
                  name="difficulty" 
                  value={currentQuestion.difficulty} 
                  onChange={handleInputChange}
                >
                  {difficulties.map(difficulty => (
                    <option key={difficulty.id} value={difficulty.id}>
                      {difficulty.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="options-section">
              <label>Options</label>
              {currentQuestion.options.map((option, index) => (
                <div key={index} className="option-group">
                  <div className="option-radio">
                    <input 
                      type="radio" 
                      name="correctAnswer" 
                      value={index} 
                      checked={currentQuestion.correctAnswer === index} 
                      onChange={() => setCurrentQuestion({...currentQuestion, correctAnswer: index})} 
                    />
                  </div>
                  <div className="option-letter">
                    {['A', 'B', 'C', 'D'][index]}
                  </div>
                  <input 
                    type="text" 
                    value={option} 
                    onChange={(e) => handleOptionChange(index, e.target.value)} 
                    placeholder={`Option ${['A', 'B', 'C', 'D'][index]}`} 
                    required 
                  />
                </div>
              ))}
              <p className="help-text">Sélectionnez la bonne réponse en cochant le bouton radio correspondant.</p>
            </div>
            
            <div className="form-group">
              <label>Explication</label>
              <textarea 
                name="explanation" 
                value={currentQuestion.explanation} 
                onChange={handleInputChange} 
                placeholder="Expliquez pourquoi cette réponse est correcte" 
                rows="3" 
                required 
              />
            </div>
            
            <div className="form-actions">
              <button 
                type="button" 
                className="save-button" 
                onClick={saveQuestion}
              >
                Enregistrer
              </button>
              <button 
                type="button" 
                className="cancel-button" 
                onClick={cancelEdit}
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Filtres et barre de recherche */}
          <div className="admin-controls">
            <div className="search-bar">
              <input 
                type="text" 
                placeholder="Rechercher une question..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div className="filter-controls">
              <select 
                value={categoryFilter} 
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="">Toutes les catégories</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
              
              <select 
                value={difficultyFilter} 
                onChange={(e) => setDifficultyFilter(e.target.value)}
              >
                <option value="">Toutes les difficultés</option>
                {difficulties.map(difficulty => (
                  <option key={difficulty.id} value={difficulty.id}>
                    {difficulty.name}
                  </option>
                ))}
              </select>
              
              <button className="create-button" onClick={createQuestion}>
                + Nouvelle Question
              </button>
            </div>
          </div>
          
          {/* Liste des questions */}
          <div className="questions-list">
            {loading ? (
              <div className="loading">Chargement des questions...</div>
            ) : filteredQuestions.length === 0 ? (
              <div className="no-results">
                {searchTerm || categoryFilter || difficultyFilter ? 
                  "Aucune question ne correspond aux critères de recherche" : 
                  "Aucune question n'est disponible"}
              </div>
            ) : (
              filteredQuestions.map(question => (
                <div key={question.id} className="question-card">
                  <div className="question-header">
                    <div className="question-category">
                      <span className="category-icon">
                        {getCategoryIcon(question.category)}
                      </span>
                      <span className="category-text">
                        {getCategoryText(question.category)}
                      </span>
                    </div>
                    <div 
                      className="question-difficulty" 
                      style={{ 
                        backgroundColor: getDifficultyColor(question.difficulty),
                        opacity: 0.8
                      }}
                    >
                      {getDifficultyText(question.difficulty)}
                    </div>
                  </div>
                  
                  <div className="question-content">
                    <h3>{question.question}</h3>
                    
                    <div className="question-options">
                      {question.options.map((option, index) => (
                        <div 
                          key={index} 
                          className={`question-option ${index === question.correctAnswer ? 'correct-option' : ''}`}
                        >
                          <span className="option-letter">{['A', 'B', 'C', 'D'][index]}</span>
                          <span className="option-text">{option}</span>
                        </div>
                      ))}
                    </div>
                    
                    <div className="question-explanation">
                      <strong>Explication:</strong> {question.explanation}
                    </div>
                  </div>
                  
                  <div className="question-actions">
                    <button 
                      className="edit-button" 
                      onClick={() => editQuestion(question)}
                      title="Modifier cette question"
                    >
                      <IconEdit /> Modifier
                    </button>
                    <button 
                      className="delete-button" 
                      onClick={() => deleteQuestion(question.id)}
                      title="Supprimer cette question"
                    >
                      <IconDelete /> Supprimer
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default QuizAdmin;